import React, { useContext, useMemo } from 'react';
import {
  Divider,
  Icon,
  Input,
  ListingTable as Listings,
  Spin,
} from 'components';
import styled from 'styled-components';
import { AuthContext, ListingLabelContext } from 'contexts';
import { useHistory } from 'react-router-dom';
import { APPT_DESK_BILLING_STATUS, TOGGLE_CARD_HEIGHT } from '../constants';
import ManageListingsContext from './ManageListingsContext';
import AppointmentDeskContext from '../AppointmentDeskContext';

const TABLE_HEADER_HEIGHT = 47; // antd value
const SEARCH_BAR_HEIGHT = 60; // antd value for input size === 'large' + margin
const PAGINATION_HEIGHT = 64; // 32px + 32px margin

const ListingTable: React.FC = () => {
  const {
    containerRef,
    footerRef,
    searchResults: { listings, count },
    loading,
    queryParameter,
    setQueryParameter,
    sort,
  } = useContext(ManageListingsContext);
  const { auth } = useContext(AuthContext);
  const { getAvailability } = useContext(ListingLabelContext);
  const { subscriptionType } = useContext(AppointmentDeskContext);
  const history = useHistory();

  const tableHeightOffset = useMemo(() => {
    const container = containerRef.current;
    const footer = footerRef.current;
    return (
      (container?.getBoundingClientRect().top ?? 0) +
      (footer?.getBoundingClientRect().height ?? 0) +
      TOGGLE_CARD_HEIGHT +
      TABLE_HEADER_HEIGHT +
      SEARCH_BAR_HEIGHT +
      PAGINATION_HEIGHT +
      2 // the height for the 2 divider line
    );
  }, [containerRef.current, footerRef.current]);

  const filterOptions = useMemo(
    () => ({
      useAppointmentDesk: [
        {
          text: 'Enabled',
          value: true,
        },
        {
          text: 'Disabled',
          value: false,
        },
      ],
      availability: Object.values(APPT_DESK_BILLING_STATUS)?.map(value => ({
        text: getAvailability(value, {
          condensed: true,
        }),
        value,
      })),
      location: auth.org.locations
        ?.filter(
          (_, index) =>
            subscriptionType === 'org' || index === parseInt(auth.location, 10),
        )
        .map((location, index) => ({
          text: location.name,
          value: index,
        })),
    }),
    [auth.org.locations, getAvailability, subscriptionType],
  );

  return (
    <>
      <StyledDivider />
      <StyledSearchBar
        allowClear
        onChange={({ target: { value } }) =>
          setQueryParameter({
            ...queryParameter,
            searchTerm: value,
          })
        }
        value={queryParameter.searchTerm}
        size="large"
        placeholder="Search addresses"
        prefix={
          <div className="icon-wrapper">
            {loading ? (
              <Spin size="small" />
            ) : (
              <Icon
                type="search"
                height={17}
                width={17}
                offset={[-1, 2]}
                color="black"
              />
            )}
          </div>
        }
        data-cy="listing-table-search"
      />
      <StyledDivider />
      <div key={tableHeightOffset}>
        <Listings
          listings={listings}
          loading={loading}
          setRedirect={address => history.replace(address)}
          heightOffset={tableHeightOffset}
          sort={sort}
          sortField={queryParameter.sortData.field}
          sortOrder={
            queryParameter.sortData.order as 'descend' | 'ascend' | null
          }
          showSorter
          isFromLiveConciergePage
          onChange={(_, filter) => {
            setQueryParameter(prev => ({
              ...prev,
              filter: {
                ...prev.filter,
                useAppointmentDesk: filter.useAppointmentDesk as boolean[],
                availability: filter.availability as string[],
                location: filter.location as string[],
              },
            }));
          }}
          pagination={{
            current: queryParameter.pageNumber + 1,
            pageSize: 20,
            total: count,
            onChange: (pageNumber: number) => {
              setQueryParameter(prev => ({
                ...prev,
                pageNumber: pageNumber - 1,
              }));
            },
            disabled: loading,
            position: ['bottomCenter'],
            showSizeChanger: false,
            // add data-cy to each pagination element
            itemRender: (page, type, element: any) => ({
              ...element,
              props: {
                ...element.props,
                'data-cy': `listing-pagination-${type}-${page}`,
              },
            }),
          }}
          filtersOption={filterOptions}
        />
      </div>
    </>
  );
};

export default ListingTable;

const StyledSearchBar = styled(Input)`
  margin: 10px;
  & > span {
    border-radius: 2px;
  }
  ${Spin} {
    margin-left: 5px;
    margin-right: 1px;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0px;
`;
